import { memo } from "react";
import ReactMarkdown from "react-markdown";

import { Tip } from "@/components/ui/overlays";
import { useGraph } from "@/stores/graph";
import { workspace } from "@/components/workspace/controller";
import { openWikiLink, openWikiLinkInBackground } from "@/lib/wikilink";
import { parseRoute } from "@/lib/url-state";
import { useTabs } from "@/stores/tabs";

/** Open an in-app hash route without a page load. Unrecognised routes fall
 * through to the browser, so a link to something this build doesn't know about
 * still goes somewhere rather than being swallowed.
 *
 * `background` is ⌘/Ctrl-click: the note becomes a tab in THIS app's dock and
 * you stay where you are. Never a browser tab — that boots a second studio. */
function followAppRoute(href: string, { background = false } = {}): void {
  const route = parseRoute(href);
  const tabs = useTabs.getState();
  if (route?.tab === "doc" && route.docId) {
    if (background) {
      if (route.docKind === "node") workspace.openMarkdown(route.docId, "", { background });
      else workspace.openDoc({ id: route.docId, title: "", docKind: "document" }, { background });
    } else if (route.docKind === "node") {
      tabs.openMarkdown(route.docId, "");
    } else {
      tabs.openReader(route.docId, "");
    }
    return;
  }
  if (route?.tab === "reader" && route.docId) {
    tabs.openReader(route.docId, "");
    return;
  }
  if (route?.tab === "orbits" || route?.tab === "graph") {
    tabs.activate(route.tab === "orbits" ? "orbits" : "graph");
    if (route.nodeId) useGraph.getState().requestFocus(route.nodeId);
    return;
  }
  window.location.hash = href;
}

import {
  MARKDOWN_REHYPE_PLUGINS,
  MARKDOWN_REMARK_PLUGINS,
  remarkWikiLink,
} from "./markdown-plugins";

// The shared pipeline plus the one plugin only an in-app surface can honour:
// `[[wikilink]]` resolution needs the atlas to resolve INTO.
const REMARK_PLUGINS = [...MARKDOWN_REMARK_PLUGINS, remarkWikiLink];

/** Backend citations come as [node:<id>] — turn them into focusable chips.
 * Models sometimes restyle the brackets (【node:…】, (node:…)), so match any
 * of them. */
function withCitations(text: string): string {
  return text.replace(/[[【(]node:([0-9a-f]{6,})[\]】)]/g, "[§](#node-$1)");
}

export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <div className="prose-gm">
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
        components={{
          a: ({ href, children, ...props }) => {
            if (href?.startsWith("#node-")) {
              const nodeId = href.slice("#node-".length);
              return (
                <Tip label="Show in atlas">
                  <button
                    type="button"
                    className="citation-chip"
                    aria-label="Show in atlas"
                    onClick={() => {
                      useTabs.getState().activate("graph");
                      useGraph.getState().requestFocus(nodeId);
                    }}
                  >
                    §{nodeId.slice(0, 4)}
                  </button>
                </Tip>
              );
            }
            // [[wikilink]] — resolve to a node and open it. ⌘/Ctrl-click opens
            // it as a BACKGROUND tab in this app's dock (the same thing the
            // homepage's cards do), not a browser tab: a second copy of the
            // studio is never what "open that note too" means.
            if (href?.startsWith("#wiki:")) {
              const label = decodeURIComponent(href.slice("#wiki:".length));
              return (
                <a
                  href={href}
                  className="wikilink"
                  onClick={(e) => {
                    e.preventDefault();
                    if (e.metaKey || e.ctrlKey) void openWikiLinkInBackground(label);
                    else void openWikiLink(label);
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            // An in-app route written as a normal markdown link
            // (`[the note](#/doc?id=…&kind=node)`). Following it in the SAME
            // browser tab would reload the whole studio; route it instead, and
            // let ⌘/Ctrl-click open it as a background tab in the dock.
            if (href?.startsWith("#/")) {
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    if (e.button !== 0) return;
                    if (e.metaKey || e.ctrlKey) {
                      e.preventDefault();
                      followAppRoute(href, { background: true });
                      return;
                    }
                    // Shift (new window) is the browser's to handle.
                    if (e.shiftKey) return;
                    e.preventDefault();
                    followAppRoute(href);
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" {...props}>
                {children}
              </a>
            );
          },
        }}
      >
        {withCitations(text)}
      </ReactMarkdown>
    </div>
  );
});
